import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { formatViews, formatDate } from "@/utils/format";

export default function ArticlePage() {
  const { slug = "" } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const [copied, setCopied] = useState(false);

  const toc = useMemo(
    () =>
      article
        ? article.content
            .map((b, i) => ({ ...b, i }))
            .filter((b) => b.type === "h2")
        : [],
    [article],
  );

  const related = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.slug !== article.slug && a.category === article.category)
            .concat(articles.filter((a) => a.slug !== article.slug && a.category !== article.category))
            .slice(0, 3)
        : [],
    [article],
  );

  const ranking = useMemo(() => [...articles].sort((a, b) => b.views - a.views).slice(0, 5), []);

  useSeo({
    title: article ? `${article.title} | 爆瓜爆料社` : "内容不存在 | 爆瓜爆料社",
    description: article ? article.excerpt : "没有找到对应的内容。",
    keywords: article ? `爆瓜爆料,${article.categoryName},${article.tags.join(",")}` : "爆瓜爆料",
    canonical: `/article/${slug}`,
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { "@type": "Person", name: article.author },
            publisher: { "@type": "Organization", name: "爆瓜爆料社" },
            articleSection: article.categoryName,
            keywords: article.tags.join(","),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://app-test.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: article.categoryName,
                item: `https://app-test.cn/category/${article.category}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `https://app-test.cn/article/${article.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!article) {
    return (
      <div className="mx-auto max-w-[1240px] px-4 py-20 text-center md:px-6">
        <i className="ri-file-warning-line text-5xl text-background-400" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
          这条爆料已经找不到了
        </h1>
        <p className="mt-2 text-sm text-foreground-500">它可能被编辑下架，或者链接有误。</p>
        <Link
          to="/"
          className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          回到首页
        </Link>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: article.categoryName, to: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <header>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-price-tag-3-fill" aria-hidden="true" />
              {article.categoryName}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 border-b border-background-200 pb-5 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" aria-hidden="true" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true" />
                {formatDate(article.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" aria-hidden="true" />
                {formatViews(article.views)} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true" />
                {article.readTime}
              </span>
            </div>
          </header>

          <div className="mt-6 h-[260px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[400px]">
            <img
              src={article.cover}
              alt={`${article.title} - 配图`}
              title={`${article.title} - ${article.categoryName}`}
              className="h-full w-full object-cover"
            />
          </div>

          <p className="mt-6 rounded-lg border-l-4 border-accent-400 bg-accent-50/70 p-4 text-sm leading-7 text-foreground-700">
            {article.excerpt}
          </p>

          {toc.length > 0 && (
            <nav
              aria-label="内容目录"
              className="mt-6 rounded-lg border border-background-200/80 bg-background-100/60 p-5"
            >
              <h2 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                <i className="ri-list-check-2 text-primary-500" aria-hidden="true" />
                内容目录
              </h2>
              <ul className="space-y-2">
                {toc.map((b) => (
                  <li key={b.i}>
                    <a
                      href={`#section-${b.i}`}
                      className="cursor-pointer text-sm text-foreground-600 transition-colors hover:text-primary-600"
                    >
                      {b.text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <div className="mt-8">
            {article.content.map((block, i) => {
              if (block.type === "h2") {
                return (
                  <h2
                    key={i}
                    id={`section-${i}`}
                    className="mt-8 scroll-mt-28 font-heading text-xl font-bold text-foreground-950"
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "quote") {
                return (
                  <blockquote
                    key={i}
                    className="my-6 rounded-lg bg-secondary-100/70 p-5 font-heading text-base italic leading-8 text-secondary-900"
                  >
                    <i className="ri-double-quotes-l mr-1 text-secondary-400" aria-hidden="true" />
                    {block.text}
                  </blockquote>
                );
              }
              return (
                <p key={i} className="mt-5 text-base leading-8 text-foreground-700">
                  {block.text}
                </p>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
            <span className="text-sm font-medium text-foreground-600">相关标签</span>
            {article.tags.map((t) => (
              <span
                key={t}
                className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-accent-100 hover:text-accent-800"
              >
                #{t}
              </span>
            ))}
            <button
              type="button"
              onClick={copyLink}
              className="ml-auto flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300/80 px-4 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className={copied ? "ri-check-line" : "ri-link"} aria-hidden="true" />
              {copied ? "链接已复制" : "复制链接"}
            </button>
          </div>
        </div>

        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
            <h2 className="mb-4 flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-bar-chart-box-fill text-primary-500" aria-hidden="true" />
              阅读热榜
            </h2>
            <ol className="flex flex-col">
              {ranking.map((a, idx) => (
                <li key={a.slug}>
                  <Link
                    to={`/article/${a.slug}`}
                    className="group flex cursor-pointer items-start gap-3 border-b border-background-100 py-3 last:border-0"
                  >
                    <span
                      className={`font-heading text-base font-bold leading-none ${
                        idx < 3 ? "text-primary-500" : "text-background-400"
                      }`}
                    >
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                      {a.title}
                    </h3>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
            <h2 className="mb-1 flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-shield-line text-accent-600" aria-hidden="true" />
              吃瓜提示
            </h2>
            <p className="mt-3 text-sm leading-6 text-foreground-600">
              本站内容为公开信息整理与编辑观察，涉及当事人未证实的部分均已标注。请理性阅读，不传播未经核实的信息。
            </p>
          </div>
        </aside>
      </div>

      <section className="mt-14">
        <h2 className="mb-5 flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-xl font-bold text-foreground-950">
          <i className="ri-bookmark-3-line text-primary-500" aria-hidden="true" />
          接着吃这个瓜
        </h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((a) => (
            <ArticleCard key={a.slug} article={a} />
          ))}
        </div>
      </section>
    </article>
  );
}